import { storage, ns } from './storage'

/**
 * 后端调用的唯一出口。
 *
 * ⚠️ AGENTS.md §0.3：离线时 `fetch` 抛的是 `TypeError`，不是 HTTP 错误。
 * 「401 就跳登录页」那套拦截器在断网时根本不触发，所以这里把「断网」和
 * 「未授权」显式分成两种结果，让调用方无法忽略其中一种。
 */

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; kind: 'offline' | 'unauthorized' | 'forbidden' | 'server'; message: string }

/**
 * 令牌持久化到本地存储 —— 董事长 2026-08-25：输一次密码就保持登录。
 *
 * 服务端会话有 90 天有效期且每次访问自动续期，所以只要还在用就不会被登出。
 * 存取一律走 createSafeStorage：隐私模式下写入会失败，但只能降级成「这次要重新
 * 输密码」，绝不能白屏（AGENTS.md §7）。
 *
 * ⚠️ 代价要说清楚：拿到这台已解锁手机的人就能直接进。M3 接 passkey 后，
 * 可以在设置里加一道刷脸二次确认，那时再收紧。
 */
const TOKEN_KEY = ns.key('auth_token')
const ROLE_KEY = ns.key('auth_role')

let token: string | null = storage.get(TOKEN_KEY, null)
let role: 'carson' | 'neo' | null = storage.get(ROLE_KEY, null) as 'carson' | 'neo' | null

export const session = {
  get token() { return token },
  get role() { return role },
  get authed() { return token !== null },
  clear() {
    token = null; role = null
    storage.remove(TOKEN_KEY); storage.remove(ROLE_KEY)
  },
}

async function call<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  let res: Response
  try {
    res = await fetch(path, {
      ...init,
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    })
  } catch {
    return { ok: false, kind: 'offline', message: '现在连不上，等有网了再试' }
  }
  if (res.status === 401) {
    session.clear()
    return { ok: false, kind: 'unauthorized', message: '需要重新解锁' }
  }
  if (res.status === 403) return { ok: false, kind: 'forbidden', message: '这个角色看不了这一页' }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    return { ok: false, kind: 'server', message: body?.error ?? `出错了（${res.status}）` }
  }
  return { ok: true, data: (await res.json()) as T }
}

export const api = {
  get: <T>(path: string) => call<T>(path),
  post: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),

  async unlock(password: string): Promise<ApiResult<{ role: 'carson' | 'neo' }>> {
    const r = await call<{ token: string; role: 'carson' | 'neo' }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    })
    if (!r.ok) {
      // 密码错走的是 401，但这里不是「会话过期」而是「这次输错了」，措辞要分开
      if (r.kind === 'unauthorized') return { ok: false, kind: 'unauthorized', message: '密码不对' }
      return r
    }
    token = r.data.token
    role = r.data.role
    storage.set(TOKEN_KEY, token)
    storage.set(ROLE_KEY, role)
    return { ok: true, data: { role: r.data.role } }
  },
}

export interface NavTab { key: string; label: string; ready: boolean }
